import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

// The report renders inside the detail pane's own scroller, so that element is the observer
// root — a viewport root never fires as the pane scrolls beneath a stationary window.
export const scrollParentOf = (node: HTMLElement | null): HTMLElement | null => {
	let element = node?.parentElement ?? null;
	while (element) {
		const overflow = getComputedStyle(element).overflowY;
		if (overflow === 'auto' || overflow === 'scroll') return element;
		element = element.parentElement;
	}
	return null;
};

const sectionNodes = (root: HTMLElement, ids: string[]): HTMLElement[] =>
	ids
		.map((id) => root.querySelector<HTMLElement>(`[data-report-section="${id}"]`))
		.filter((node): node is HTMLElement => node !== null);

// A smooth jump crosses several sections and can settle without a final crossing, so the
// observer's last word is a section short — and a section near the end can never reach the spy
// line at all, because there is not enough report left below it. `pin` holds the clicked
// section lit until the scroll stops; the observer takes over again on the next scroll.
// `scrollend` does the releasing where it exists (not Safari before 18), hence the cap.
const PIN_CAP_MS = 2500;

// Returns the id of the section currently under the sticky nav, and a way to pin one. The
// observer is only the trigger — the active section is read from the live rects, so a section
// taller than the pane can never leave the nav lit on its neighbour.
export const useScrollSpy = (
	rootRef: RefObject<HTMLElement>,
	ids: string[],
	offset: number,
): { activeId: string | null; pin: (id: string) => void } => {
	const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
	const pinned = useRef(false);
	const release = useRef<() => void>(() => {});

	useEffect(() => () => release.current(), []);

	const pin = useCallback(
		(id: string) => {
			setActiveId(id);
			release.current();
			pinned.current = true;

			const scroller = scrollParentOf(rootRef.current);
			const stop = () => {
				pinned.current = false;
				window.clearTimeout(timer);
				scroller?.removeEventListener('scrollend', stop);
				scroller?.removeEventListener('wheel', stop);
				scroller?.removeEventListener('touchmove', stop);
				release.current = () => {};
			};
			const timer = window.setTimeout(stop, PIN_CAP_MS);
			scroller?.addEventListener('scrollend', stop, { once: true });
			scroller?.addEventListener('wheel', stop, { once: true, passive: true });
			scroller?.addEventListener('touchmove', stop, { once: true, passive: true });
			release.current = stop;
		},
		[rootRef],
	);

	useEffect(() => {
		const root = rootRef.current;
		if (!root || ids.length === 0) return undefined;

		const scroller = scrollParentOf(root);
		const nodes = sectionNodes(root, ids);
		if (nodes.length === 0) return undefined;

		const recompute = () => {
			if (pinned.current) return;
			// At maximum scroll the final section can sit entirely below the spy line, which would
			// otherwise leave its pill unreachable however far the reader scrolls.
			const atBottom =
				scroller !== null &&
				scroller.scrollHeight > scroller.clientHeight &&
				scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
			if (atBottom) {
				setActiveId(nodes[nodes.length - 1].dataset.reportSection ?? null);
				return;
			}
			const line = (scroller ? scroller.getBoundingClientRect().top : 0) + offset;
			let current = nodes[0];
			nodes.forEach((node) => {
				if (node.getBoundingClientRect().top <= line) current = node;
			});
			setActiveId(current.dataset.reportSection ?? null);
		};

		if (typeof IntersectionObserver === 'undefined') {
			recompute();
			return undefined;
		}

		const observer = new IntersectionObserver(recompute, { root: scroller, threshold: [0, 1] });
		nodes.forEach((node) => observer.observe(node));
		return () => observer.disconnect();
	}, [rootRef, ids, offset]);

	return { activeId, pin };
};
